import React, { useState } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { FORM_IDS, useLoginContext } from 'providers/LoginProvider';
import { useFormValidation } from '../loginValidation';
import { useFormInputHandler, hasValue } from 'swipeclock-frontend';
import { FormInputGrid } from '../components/shared/StyledComponents';
import WhiteLabelLogo from '../components/shared/WhiteLabelLogo';
import PoweredByLabel from '../components/shared/PoweredByLabel';
import UsernamePassword from '../components/DefaultLogin/UsernamePassword';
import SiteId from '../components/DefaultLogin/SiteId';
import BottomButtons from '../components/DefaultLogin/BottomButtons';
import AnimatedAlert from '../components/shared/AnimatedAlert';
import { useWhiteLabelContext } from 'providers/WhiteLabelProvider';
import { useHandleLogin } from '../hooks/useLoginUser';

export default function LoginPage() {
  const { state: whiteLabelState } = useWhiteLabelContext();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  // State
  const [submitting, setSubmitting] = useState(false);

  // Hooks
  const loginCallback = useHandleLogin();
  const {
    actions,
    meta: { pages },
  } = useLoginContext();

  const { errorContext, validationCallbackLogin } = useFormValidation();

  const loginBag = useFormInputHandler({
    errorContext,
    ids: [FORM_IDS.USERNAME, FORM_IDS.PASSWORD, FORM_IDS.SITEID],
    validationCallback: validationCallbackLogin,
  });
  const { isValid, validationRef } = loginBag;

  // Chrome hides autofilled values from JS until the user interacts with the
  // page, so track MUI's autofill animation to know the fields are filled.
  const [autofilledIds, setAutofilledIds] = useState({});
  const isAutofilled =
    autofilledIds[FORM_IDS.USERNAME] === true &&
    autofilledIds[FORM_IDS.PASSWORD] === true;

  const handleAutofill = (event) => {
    const { id } = event.target;
    if (event.animationName === 'mui-auto-fill') {
      setAutofilledIds((prev) => ({ ...prev, [id]: true }));
    } else if (event.animationName === 'mui-auto-fill-cancel') {
      setAutofilledIds((prev) => ({ ...prev, [id]: false }));
    }
  };

  // Event Handlers
  const handleLogin = async (isWebClockLogin = true) => {
    try {
      const valid = isValid(pages.START);
      if (!valid) {
        throw new Error('Form is invalid.');
      }

      const values = extractValues(validationRef);
      await loginCallback({
        values,
        setSubmitting,
        whiteLabelState,
        isWebClockLogin,
      });

      return;
    } catch (error) {
      console.error(error);
      const errorText = 'Invalid form.  Fill out the form before continuing.';
      actions.setGlobalMessage({ message: errorText, severity: 'error' });
    }
  };

  return (
    <FormInputGrid
      raised={!isMobile}
      noHeader={whiteLabelState?.noHeader}
      onAnimationStart={handleAutofill}
    >
      <WhiteLabelLogo whiteLabelState={whiteLabelState} />
      <AnimatedAlert />
      <UsernamePassword loginBag={loginBag} handleLogin={handleLogin} />
      <SiteId loginBag={loginBag} />
      <BottomButtons
        loginBag={loginBag}
        submitting={submitting}
        handleLogin={handleLogin}
        isAutofilled={isAutofilled}
      />
      <PoweredByLabel whiteLabelState={whiteLabelState} />
    </FormInputGrid>
  );
}

/** helper functions */

function extractValues(validationRef) {
  return Object.keys(validationRef.current).reduce((acc, key) => {
    const keyValue = validationRef.current[key]?.value;
    acc[key] = hasValue(keyValue) ? keyValue : null;
    return acc;
  }, {});
}
